import React from 'react';

/**
 * Execute a callback at most every `delay` milliseconds.
 * Any subsequent call to the returned function will resolve the previous one and schedule a new one.
 * The previous call will be resolved with the result of the most recent call.
 * `callback` will only be called AFTER `delay` has passed since the last call to the function returned by this hook.
 * @template T
 * @param {(...args: any[]) => T} callback - The function to be executed
 * @param {number} delay - The minimum delay between calls
 * @returns {(...args: any[]) => Promise<T>} - The function that will execute the callback after `delay`, cancelling any previous call
 */
const executeAtMostMs = (callback, delay) => {
  let timeout;
  let previousResult;
  let latestPromiseResolver;

  return async (...args) => new Promise(resolve => {
    if (timeout) {
      clearTimeout(timeout);
      latestPromiseResolver(previousResult);
    }

    latestPromiseResolver = resolve;
    timeout = setTimeout(() => {
      timeout = null;
      previousResult = callback(...args);
      resolve(previousResult);
    }, delay);
  });
};

/**
 * @description This hook is just a wrapper around `executeAtMostMs` that memoizes the function.
 * @see {@link executeAtMostMs}
 */
const useExecuteAtMostMs = (callback, delay) => React.useMemo(
  () => executeAtMostMs(callback, delay),
  [callback, delay]
);

export default useExecuteAtMostMs;
